import { useState, useEffect, useCallback } from "react";
import { Loader2, ArrowRight, AlertTriangle, CheckCircle2, HelpCircle, Trash2 } from "lucide-react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";

function currency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

// Rows the milestone ledger can actually correct. Everything else is reported
// for a human to decide on.
const CORRECTABLE = ["already_paid", "partially_paid"];

const GROUPS = [
  {
    key: "already_paid",
    icon: CheckCircle2,
    title: "Fully paid — still showing as owed",
    blurb: "Every payment milestone on these projects is marked paid. The Money tab never received the update.",
  },
  {
    key: "partially_paid",
    icon: CheckCircle2,
    title: "Partly paid — balance overstated",
    blurb: "Some payments came in after the receivable was created. The remaining balance is smaller than it looks.",
  },
  {
    key: "orphaned",
    icon: Trash2,
    title: "Orphaned — the project was deleted",
    blurb: "These receivables point at projects that no longer exist in HQ, so they can never be reconciled. Deleting them is the only way to clear them.",
  },
  {
    key: "no_milestones",
    icon: HelpCircle,
    title: "No payment tracker set up",
    blurb: "These projects have no payment milestones, so there is nothing to check them against. They stay as-is — confirm these by hand.",
  },
];

function Row({ row, selectable, checked, onToggle }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0">
      {selectable && (
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          className="mt-1 flex-shrink-0"
          aria-label={`Include ${row.program_name}`}
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm truncate">{row.program_name}</p>
        <p className="text-xs text-muted-foreground truncate">
          {row.client_name}
          {row.due_date ? ` · due ${row.due_date.slice(0, 10)}` : " · no due date"}
          {row.milestone_count > 0 && ` · ${currency(row.milestone_paid_total)} paid across ${row.milestone_count} milestone(s)`}
        </p>
      </div>
      <div className="text-right flex-shrink-0 tabular-nums">
        {row.delta > 0 ? (
          <>
            <p className="text-sm flex items-center justify-end gap-1.5">
              <span className="text-muted-foreground line-through">{currency(row.current_outstanding)}</span>
              <ArrowRight className="w-3 h-3 text-muted-foreground" strokeWidth={1.5} />
              <span className={row.new_outstanding > 0 ? "" : "text-green-700"}>
                {currency(row.new_outstanding)}
              </span>
            </p>
            <p className="text-[11px] text-muted-foreground">
              records {currency(row.delta)} collected
            </p>
          </>
        ) : (
          <p className="text-sm">{currency(row.current_outstanding)}</p>
        )}
      </div>
    </div>
  );
}

export default function ReconcileDialog({ open, onClose, onApplied }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [excluded, setExcluded] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/receivables/reconcile/preview");
      setReport(res.data);
      setExcluded(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not build the reconciliation report");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [onClose]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const rows = report?.rows || [];
  const grouped = GROUPS.map(g => ({ ...g, rows: rows.filter(r => r.classification === g.key) }))
    .filter(g => g.rows.length > 0);

  const correctable = rows.filter(r => CORRECTABLE.includes(r.classification) && r.delta > 0);
  const selected = correctable.filter(r => !excluded.has(r.receivable_id));
  const orphans = rows.filter(r => r.classification === "orphaned");

  // Headline reflects the current selection, not the whole report, so
  // unchecking a row updates the projected Past Due immediately.
  const currentPastDue = report?.totals?.current_past_due || 0;
  const released = selected
    .filter(r => r.is_past_due)
    .reduce((sum, r) => sum + (r.current_outstanding - r.new_outstanding), 0);
  const projectedPastDue = currentPastDue - released;

  function toggle(id) {
    setExcluded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleApply() {
    if (!selected.length) return;
    setApplying(true);
    try {
      const res = await apiClient.post("/receivables/reconcile/apply", {
        receivable_ids: selected.map(r => r.receivable_id),
      });
      toast.success(`Reconciled ${res.data.applied} receivable(s)`, {
        description: `${currency(res.data.past_due_released)} cleared from Past Due`,
      });
      onApplied?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply");
    } finally {
      setApplying(false);
    }
  }

  async function handleDeleteOrphans() {
    if (!orphans.length) return;
    const total = orphans.reduce((sum, r) => sum + r.current_outstanding, 0);
    const ok = window.confirm(
      `Permanently delete ${orphans.length} orphaned receivable(s) totalling ${currency(total)}?\n\n` +
      `Their projects no longer exist in HQ. This cannot be undone.`
    );
    if (!ok) return;

    setDeleting(true);
    try {
      const res = await apiClient.post("/receivables/reconcile/delete-orphans", {
        receivable_ids: orphans.map(r => r.receivable_id),
      });
      toast.success(`Deleted ${res.data.deleted} orphaned receivable(s)`);
      onApplied?.();
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "var(--font-sans)" }}>Reconcile receivables</DialogTitle>
          <DialogDescription>
            Compares the Money tab against payments recorded in each project's payment tracker.
            Nothing is saved until you apply.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Headline */}
            <div className="grid grid-cols-3 gap-3 py-3 border-y border-border">
              <div>
                <p className="micro-label">Past Due now</p>
                <p className="text-xl sm:text-2xl tabular-nums">{currency(currentPastDue)}</p>
              </div>
              <div>
                <p className="micro-label">Already collected</p>
                <p className="text-xl sm:text-2xl tabular-nums text-green-700">{currency(released)}</p>
              </div>
              <div>
                <p className="micro-label">Past Due after</p>
                <p className="text-xl sm:text-2xl tabular-nums">{currency(projectedPastDue)}</p>
              </div>
            </div>

            {/* Groups */}
            <div className="overflow-y-auto flex-1 -mx-1 px-1">
              {grouped.map(group => {
                const Icon = group.icon;
                const selectable = CORRECTABLE.includes(group.key);
                const groupRows = selectable ? group.rows.filter(r => r.delta > 0) : group.rows;
                if (!groupRows.length) return null;
                const groupTotal = groupRows.reduce((sum, r) => sum + r.current_outstanding, 0);

                return (
                  <div key={group.key} className="mt-5 first:mt-3">
                    <div className="flex items-start gap-2 mb-1">
                      <Icon className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                      <div className="min-w-0">
                        <p className="text-sm">
                          {group.title}
                          <span className="text-muted-foreground"> · {groupRows.length} · {currency(groupTotal)}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">{group.blurb}</p>
                      </div>
                    </div>

                    {group.key === "orphaned" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeleteOrphans}
                        disabled={deleting}
                        className="my-2 h-8 text-xs"
                        data-testid="delete-orphans-btn"
                      >
                        {deleting
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                        <span className="ml-1.5">Delete these {groupRows.length}</span>
                      </Button>
                    )}

                    <div className="mt-1">
                      {groupRows.map(row => (
                        <Row
                          key={row.receivable_id}
                          row={row}
                          selectable={selectable}
                          checked={!excluded.has(row.receivable_id)}
                          onToggle={() => toggle(row.receivable_id)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              {!correctable.length && !orphans.length && (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  Everything already matches the payment trackers. Nothing to reconcile.
                </p>
              )}

              {report?.totals?.waived_total > 0 && (
                <p className="text-xs text-amber-600 mt-5 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                  {currency(report.totals.waived_total)} sits on milestones marked Waived. Waived money is
                  never collected, so it is not applied here — reduce the expected amount on those
                  receivables if it should not be chased.
                </p>
              )}
            </div>

            <DialogFooter className="border-t border-border pt-3">
              <p className="text-xs text-muted-foreground mr-auto self-center">
                Collected amounts only ever move up — a payment recorded by hand is never erased.
              </p>
              <Button variant="outline" onClick={onClose} disabled={applying}>Cancel</Button>
              <Button onClick={handleApply} disabled={applying || !selected.length} data-testid="reconcile-apply-btn">
                {applying && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Apply {selected.length} correction{selected.length === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
